import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Building2, Save, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { QRCodeCanvas } from "qrcode.react";

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/settings").then(({ data }) => setForm(data)); }, []);

  const save = async () => {
    if (!isAdmin) return toast.error("Only admins can update settings");
    setSaving(true);
    try {
      await api.put("/settings", {
        ...form,
        default_gst_percent: Number(form.default_gst_percent),
      });
      toast.success("Settings updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Pharmacy Settings</h1>
        <p className="text-sm text-slate-500">Business identity, GST, UPI payment info &amp; invoice branding.</p>
      </div>

      {!isAdmin && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-amber-800 text-sm">
          Only admin users can change settings. You're viewing in read-only mode.
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200 space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-700" />
            <h3 className="font-display font-bold">Business Details</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Pharmacy name" v={form.pharmacy_name} on={(v) => setForm({ ...form, pharmacy_name: v })} disabled={!isAdmin} testid="setting-pharma-name" />
            <Field label="Tagline" v={form.tagline} on={(v) => setForm({ ...form, tagline: v })} disabled={!isAdmin} />
            <Field label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} disabled={!isAdmin} />
            <Field label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} disabled={!isAdmin} />
            <div className="sm:col-span-2">
              <Field label="Address" v={form.address} on={(v) => setForm({ ...form, address: v })} disabled={!isAdmin} />
            </div>
            <Field label="GSTIN" v={form.gstin} on={(v) => setForm({ ...form, gstin: v })} disabled={!isAdmin} />
            <Field label="Drug License No." v={form.drug_license} on={(v) => setForm({ ...form, drug_license: v })} disabled={!isAdmin} />
            <Field label="Default GST %" type="number" v={form.default_gst_percent} on={(v) => setForm({ ...form, default_gst_percent: v })} disabled={!isAdmin} />
            <Field label="Currency" v={form.currency} on={(v) => setForm({ ...form, currency: v })} disabled />
          </div>

          <div className="border-t pt-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <QrCode className="w-4 h-4 text-emerald-700" />
              <h3 className="font-display font-bold">UPI Payment</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="UPI ID" v={form.upi_id} on={(v) => setForm({ ...form, upi_id: v })} disabled={!isAdmin} placeholder="yourpharma@upi" testid="setting-upi-id" />
              <Field label="Payee Name (on QR)" v={form.upi_payee_name} on={(v) => setForm({ ...form, upi_payee_name: v })} disabled={!isAdmin} />
            </div>
            <p className="text-xs text-slate-500 mt-2">Bill scanner QR is generated dynamically with each invoice's exact amount.</p>
          </div>

          <div className="border-t pt-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-emerald-700" />
              <h3 className="font-display font-bold">Delivery Area (Online Orders)</h3>
            </div>
            <Field label="Serviceable Pincodes (comma separated)" v={form.delivery_pincodes} on={(v) => setForm({ ...form, delivery_pincodes: v })} disabled={!isAdmin} placeholder="244713, 244714" testid="setting-delivery-pincodes" />
            <p className="text-xs text-slate-500 mt-2">Sirf in pincodes par website se delivery order accept hoga. Khaali chhodne par saare pincodes allowed hain.</p>
          </div>

          {isAdmin && (
            <div className="pt-2">
              <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-emerald-900 hover:bg-emerald-800">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </div>
          )}
        </Card>

        <Card className="p-6 border-slate-200 flex flex-col items-center gap-2">
          <h3 className="font-display font-bold">QR Preview (₹100)</h3>
          <p className="text-xs text-slate-500 -mt-1">Sample amount</p>
          <div className="p-3 bg-white border border-slate-200 rounded-xl mt-3">
            <QRCodeCanvas
              value={`upi://pay?pa=${encodeURIComponent(form.upi_id || "example@upi")}&pn=${encodeURIComponent(form.upi_payee_name || "Trustmeds Pharma")}&am=100.00&cu=INR&tn=Sample`}
              size={180}
              level="M"
            />
          </div>
          <div className="text-xs text-slate-500 font-mono-num mt-2">{form.upi_id || "example@upi"}</div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", disabled, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={v ?? ""} disabled={disabled} placeholder={placeholder} onChange={(e) => on(e.target.value)} className="mt-1 h-10" />
    </div>
  );
}
